import * as React from 'react';
import { CoreButton as Button } from '../internal/WixUiCoreComponents/components/CoreButton';
import { CoreInput as Input } from '../internal/WixUiCoreComponents/components/CoreInput';
import { st, classes } from './Counter.deprecated.st.css';
import { Error, ErrorSmall, Minus, Plus, } from '@wix/wix-ui-icons-common/on-stage';
import { DATA_ATTRS, DATA_HOOKS } from './dataKeys';
import { IconTooltip } from '../IconTooltip';
import { TooltipSkin } from '../Tooltip/types';
import { CounterSize, } from './types';
import { getAriaDescribedBy } from '../../common/a11y';
import { Fieldset } from '../internal/Fieldset';
import uniqueId from '../../common/uniqueId';
import { shellClasses } from '../../common/shellClasses';
/** Counter */
export class Counter extends React.Component {
    constructor(props) {
        super(props);
        this._rootRef = React.createRef();
        this._inputRef = React.createRef();
        this.state = {
            isErrorTooltipShown: false,
            focused: false,
            errorIconClicked: false,
            isWithinRoot: false,
        };
        this.focus = () => {
            this._inputRef.current.focus();
            this.setState({ focused: true });
        };
        this.blur = () => {
            this._inputRef.current.blur();
            this.setState({ focused: false });
        };
        this.showErrorTooltip = () => {
            this._toggleTooltip(true);
        };
        this.hideErrorTooltip = () => {
            this._toggleTooltip(false);
        };
        this._onDecrement = () => {
            const { step, max, onChange, value } = this.props;
            if (max || max === 0) {
                const result = Math.min(max, value - step);
                onChange(result.toString());
                return;
            }
            onChange((value - step).toString());
        };
        this._onIncrement = () => {
            const { step, min, onChange, value } = this.props;
            if (min || min === 0) {
                const result = Math.max(min, value + step);
                onChange(result.toString());
                return;
            }
            onChange((value + step).toString());
        };
        this._onRootFocus = () => {
            const { isWithinRoot, errorIconClicked } = this.state;
            if (!isWithinRoot && !errorIconClicked) {
                this.setState({ isWithinRoot: true });
                this._toggleTooltip(true);
            }
        };
        this._toggleTooltip = (showTooltip) => {
            this.setState({ isErrorTooltipShown: showTooltip });
        };
        this._onErrorIconMouseDown = () => {
            this.setState({ errorIconClicked: true });
        };
        this._onErrorIconClick = () => {
            const { isErrorTooltipShown } = this.state;
            this._toggleTooltip(!isErrorTooltipShown);
            this.setState({ errorIconClicked: false });
        };
        this._onErrorIconKeyDown = (e) => {
            if (!e.shiftKey && e.key === 'Tab') {
                // The ErrorIcon is at the edge of the component - Tab click exits the Component
                this._exitComponent();
                this.setState({ focused: false });
            }
        };
        this._onPlusKeyDown = (e) => {
            if (e.shiftKey && e.key === 'Tab') {
                // The Plus Button is at the edge of the component - Shift Tab click exits the Component
                this._exitComponent();
            }
        };
        this._exitComponent = () => {
            this._toggleTooltip(false);
            this.setState({ isWithinRoot: false });
        };
        this._onFocus = () => {
            this.setState({ focused: true });
        };
        this._onBlur = () => {
            this.setState({ focused: false });
        };
        this._onClickOutside = () => {
            if (this._rootRef.current &&
                !this._rootRef.current.contains(document.activeElement)) {
                this._exitComponent();
            }
        };
        this._getErrorIcon = () => {
            const { size } = this.props;
            const ErrorIcon = size === 'medium' ? Error : ErrorSmall;
            return React.createElement(ErrorIcon, { className: classes.error });
        };
        this._dataAttrs = () => {
            const { disabled, error } = this.props;
            const { focused } = this.state;
            return {
                [DATA_ATTRS.DISABLED]: disabled,
                [DATA_ATTRS.ERROR]: error,
                [DATA_ATTRS.FOCUSED]: focused,
            };
        };
        this._errorMessageId = uniqueId('error-message');
    }
    render() {
        const { inputAriaLabel, min, max, step, disabled, onChange, value, error, errorMessage, className, size, 'aria-describedby': ariaDescribedByProp, errorTooltipPlacement, errorTooltipMaxWidth, newErrorMessage, 'data-hook': dataHook, label, steppersType, onKeyDown, required, showAsterisk, incrementAriaLabel, decrementAriaLabel, } = this.props;
        const shouldShowErrorMessageTooltip = error && !!errorMessage && !newErrorMessage;
        const icon = this._getErrorIcon();
        const { isErrorTooltipShown } = this.state;
        const stStates = { disabled, error, newErrorMessage, size };
        const ariaDescribedBy = getAriaDescribedBy([
            ariaDescribedByProp,
            errorMessage ? this._errorMessageId : '',
        ]);
        const showLabelAsterisk = showAsterisk && required;
        return (React.createElement(Fieldset, { errorMessageId: this._errorMessageId, errorMessage: newErrorMessage && error && errorMessage, dataHook: dataHook, label: label, showLabelAsterisk: showLabelAsterisk, "aria-labelledby": this.props['aria-labelledby'], "aria-label": this.props['aria-label'], onFocus: this._onRootFocus, onLabelClick: this.focus, ref: this._rootRef, className: st(classes.root, stStates, className), ...this._dataAttrs() },
            React.createElement("div", { className: classes.group },
                React.createElement(Button, { className: st(classes.btn, classes.minus, shellClasses.hasCustomFocus), "data-hook": DATA_HOOKS.MINUS_BUTTON, onClick: this._onDecrement, name: "decrement", disabled: disabled || value - step < min, type: steppersType, tabIndex: -1, "aria-label": decrementAriaLabel || 'Decrement' },
                    React.createElement(Minus, { className: classes.btnIcon })),
                React.createElement("div", { className: classes.inputWrapper },
                    React.createElement(Input, { className: classes.counterInput, inputClassName: shellClasses.hasCustomFocus, "aria-label": inputAriaLabel, "aria-describedby": ariaDescribedBy, "aria-valuenow": value, "aria-valuetext": value.toString(), "aria-valuemin": min, "aria-valuemax": max, "aria-invalid": error || false, role: "spinbutton", onChange: (ev) => onChange(ev.target.value), onFocus: this._onFocus, onBlur: this._onBlur, onKeyDown: onKeyDown, type: "number", ref: this._inputRef, disabled: disabled, min: min, max: max, step: step, error: error, value: value.toString(), required: required })),
                shouldShowErrorMessageTooltip && (React.createElement(IconTooltip, { "data-hook": DATA_HOOKS.ERROR_TOOLTIP_ICON, message: errorMessage, icon: icon, tooltipId: this._errorMessageId, tooltipSkin: TooltipSkin.Error, showTooltip: isErrorTooltipShown, onMouseDown: this._onErrorIconMouseDown, onClick: this._onErrorIconClick, onClickOutside: this._onClickOutside, onKeyDown: this._onErrorIconKeyDown, className: classes.errorIcon, tooltipProps: {
                        placement: errorTooltipPlacement,
                        maxWidth: errorTooltipMaxWidth,
                        appendTo: 'window',
                    } })),
                React.createElement(Button, { className: st(classes.btn, classes.plus, shellClasses.hasCustomFocus), "data-hook": DATA_HOOKS.PLUS_BUTTON, onClick: this._onIncrement, onKeyDown: this._onPlusKeyDown, name: "increment", disabled: disabled || (max && value + step > max), type: steppersType, tabIndex: -1, "aria-label": incrementAriaLabel || 'Increment' },
                    React.createElement(Plus, { className: classes.btnIcon })))));
    }
}
Counter.displayName = 'Counter';
Counter.defaultProps = {
    step: 1,
    value: 0,
    size: CounterSize.medium,
    errorTooltipPlacement: 'top',
};
//# sourceMappingURL=Counter.js.map